/**
 * Class for executing promises sequentially by ID.
 * Ensures that operations with the same ID are processed in order.
 * Automatically cleans up completed operations to prevent memory leaks.
 */
export class SequentialPromiseExecutor {
  /**
   * Map tracking the last operation promise for each ID
   */
  public operations = new Map<string, Promise<unknown>>();

  /**
   * Execute a promise-returning function, ensuring sequential execution for the same ID.
   * Automatically removes the promise from tracking once it completes.
   */
  public async execute<T>(id: string, execute: () => Promise<T>): Promise<T> {
    // Get the promise for the last operation with this ID, or a resolved promise if none
    const lastOperation = this.operations.get(id) || Promise.resolve();

    // Create a new operation that starts after the previous one finishes
    const operation = lastOperation
      // Suppress errors from previous operations to prevent chain breakage
      .catch((err) => {
        console.debug(`Ignoring error from previous operation on ${id}: ${err instanceof Error ? err.stack : err}`);
      })
      .then(execute); // Execute the new operation and use its return value

    // Set up cleanup when this operation completes (success or failure)
    const cleanupOperation = operation
      // Ensure cleanup happens even if operation fails
      .catch((err) => {
        console.debug(`Ignoring error cleaning operation on ${id}: ${err instanceof Error ? err.stack : err}`);
      })
      .finally(() => {
        // Only clean up if this is still the current operation for this ID
        if (this.operations.get(id) === cleanupOperation) {
          this.operations.delete(id);
        }
      });

    // Update the last operation for this ID with the operation that includes cleanup
    this.operations.set(id, cleanupOperation);

    // Return the original operation without the cleanup logic to avoid creating
    // a circular reference that might prevent garbage collection
    return operation;
  }

  /**
   * Clear all tracked operations for all IDs.
   * This is still useful for forcibly clearing all operations when needed.
   */
  public clear(): void {
    this.operations.clear();
  }
}
